import { useState } from "react";
import { Typography } from "@mui/material";
import { AttributeCommunicationForm } from "./AttributeCommunicationForm";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Button from "@imminently/decisively-ui/Button";
import Select from "@imminently/decisively-ui/Select";
import Field from "@imminently/decisively-ui/Field";
import { useFormContext, useWatch } from "@imminently/decisively-ui/Form";
import { AttributeInfoFormValues } from "./useAttributeForm";
import {
  Trigger,
  TriggerTask,
  TriggerNotification,
  TriggerEnrichment,
  TriggerComms,
  TriggerIntegration,
  TriggerStorage,
  TriggerContact,
  TriggerEntity,
} from "@packages/commons";
import { useTranslation } from "react-i18next";
import { AttributeTaskForm } from "./AttributeTaskForm";
import { AttributeEnrichmentForm } from "./AttributeEnrichmentForm";
import { get } from "lodash";
import { scrollable } from "@/common";
import Icon from "@imminently/decisively-ui/Icon";
import { v4 as uuidv4 } from "uuid";
import { AttributeIntegrationForm } from "./AttributeIntegrationForm";
import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { AttributeStorageForm } from "./AttributeStorageForm";
import { AttributeContactForm } from "./AttributeContactForm";
import { AttributeEntityForm } from "./AttributeEntityForm";
import { AttributeNotificationForm } from "./AttributeNotificationForm";

const DropdownMenuTrigger = DropdownMenu.Trigger;
const DropdownMenuContent = DropdownMenu.Content;
const DropdownMenuItem = DropdownMenu.Item;

const defaultTrigger = (type: string): Trigger => {
  const DEFAULT_COMMS = {
    title: "",
    method: "email",
    subject: "",
    body: "",
    connection: "",
    path: "",
    restMethod: "post",
    contentType: "application/json",
    query: "",
    retryAttempts: undefined,
    scheduled: undefined,
  };

  switch (type) {
    case "task":
      return {
        type: "task",
        title: "",
        interview: "",
        assignmentMethod: "attribute",
        assignment: "",
        assignmentSubmitterField: "",
        assignmentIntegration: {
          connection: "",
          path: "",
          restMethod: "get",
          contentType: "application/json",
          query: "",
          requestMapping: "",
          assignmentMapping: "",
          responseMapping: "",
          retryAttempts: undefined,
        },
        assignmentOrgModel: {
          subjectType: "",
          subjectId: "",
          level: "",
          previousLevel: "",
          escalationExhausted: "",
        },
        dueDate: "",
        approvalLabel: "Approve",
        approvalOutcome: "true",
        rejectionLabel: "Reject",
        rejectionOutcome: "false",
        approvalMethod: "any",
        aiFirstPass: false,
        guidance: "",
        escalation: {
          attribute: "",
          subject: "",
          body: "",
        },
        communication: {
          method: "none",
          subject: "",
          body: "",
          connection: "",
          path: "",
          restMethod: "post",
          contentType: "application/json",
          query: "",
          retryAttempts: undefined,
        },
        scheduled: undefined,
      } as TriggerTask;
    case "notification":
      return {
        type: "notification",
        title: "",
        subject: "",
        body: "",
        recipient: "",
        severity: "info",
        dueDate: "",
        thread: "decisively",
        scheduled: undefined,
      } as TriggerNotification;
    case "integration":
      return {
        type: "integration",
        connection: "",
        restMethod: "get",
        contentType: "application/json",
        title: "",
        path: "",
        query: "",
        requestMapping: "",
        responseMapping: "",
        deltaMapping: "",
        retryAttempts: undefined,
        scheduled: undefined,
      } as TriggerIntegration;
    case "storage":
      return {
        type: "storage",
        container: "",
        append: false,
        title: "",
        mapping: "",
        deltaMapping: "",
      } as TriggerStorage;
    case "communication":
      return {
        type: "comms",
        ...DEFAULT_COMMS,
        who: "",
        whoMethod: "attribute",
        whoIntegration: {
          connection: "",
          path: "",
          restMethod: "get",
          contentType: "application/json",
          query: "",
          requestMapping: "",
          responseMapping: "",
          retryAttempts: undefined,
        },
        whoOrgModel: {
          subjectType: "",
          subjectId: "",
          level: "",
        },
        whoIsContact: false,
      } as TriggerComms;
    case "contact":
      return {
        type: "contact",
        path: "",
        contact: "",
        title: "Update Contact",
        template: {},
      } as TriggerContact;
    case "entity":
      return {
        type: "entity",
        title: "Update Entity",
        template: {},
      } as TriggerEntity;
    case "enrichment":
      return {
        type: "enrichment",
        title: "",
        connection: {
          type: "attribute",
          value: "",
        },
        mapping: {
          inbound: {
            type: "goal",
            value: "",
            useDescriptions: false,
          },
          outbound: {
            type: "default",
            value: "",
            useDescriptions: false,
          },
        },
        transform: {
          inbound: {
            type: "file",
            useDescriptions: false,
            value: "",
          },
          outbound: {
            type: "file",
            useDescriptions: false,
            value: "",
          },
        },
      } as TriggerEnrichment;
    default:
      throw new Error(`Unknown trigger type: ${type}`);
  }
};

const TriggerForm = ({ scope }: { scope: string }) => {
  const form = useFormContext<AttributeInfoFormValues>();
  const values = useWatch({ control: form.control });
  const trigger = get(values, scope) as Trigger;
  if (!trigger) {
    return <Typography>Trigger not found</Typography>;
  }
  switch (trigger.type) {
    case "task":
      return <AttributeTaskForm scope={scope} />;
    case "notification":
      return <AttributeNotificationForm scope={scope} />;
    case "comms":
      return <AttributeCommunicationForm scope={scope} />;
    case "integration":
      return <AttributeIntegrationForm scope={scope} />;
    case "storage":
      return <AttributeStorageForm scope={scope} />;
    case "contact":
      return <AttributeContactForm scope={scope} />;
    case "entity":
      return <AttributeEntityForm scope={scope} />;
    case "enrichment": // Is this still being used?
      return <AttributeEnrichmentForm scope={scope} />;
    default:
      // @ts-ignore, we should never get here
      return <Typography>Unknown trigger type: {trigger.type}</Typography>;
  }
};

export const AttributeTriggers = () => {
  const { t } = useTranslation();
  const form = useFormContext<AttributeInfoFormValues>();
  const values = useWatch({ control: form.control }) as AttributeInfoFormValues;
  const setFieldValue = (name: string, value: any) =>
    form.setValue(name as any, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  const canEdit = useIsProjectTechnicalWriter();
  const triggers = values?.triggers ?? {};

  const options = Object.keys(triggers).map((t, i) => ({
    label: triggers[t]?.title?.length > 0 ? triggers[t].title : `Trigger ${i + 1}`,
    value: `triggers.${t}`,
  }));

  // the current active trigger scope (string with index)
  // options.length > 0 ? options[0].value : ""
  const [trigger, setTrigger] = useState<string>("");

  const addTrigger = (type: string) => {
    const id = uuidv4();
    const newTrigger = defaultTrigger(type);
    setFieldValue(`triggers.${id}`, newTrigger);
    setTrigger(`triggers.${id}`);
  };

  const deleteTrigger = (value: string) => {
    if (!canEdit) return; // Prevent deletion if user can't edit
    const id = value.split(".")[1];
    const newTriggers = { ...triggers };
    delete newTriggers[id];
    setFieldValue("triggers", newTriggers);
    // if the deleted trigger is the current trigger, select first, otherwise unset
    if (trigger === value) {
      setTrigger(Object.keys(newTriggers).length > 0 ? `triggers.${Object.keys(newTriggers)[0]}` : "");
    }
  };

  const label = trigger.length > 0 ? undefined : options.length > 0 ? "Select a trigger" : "No triggers";

  // console.log('trigger options', trigger, options, label);

  return (
    <div className={`flex flex-col gap-4`}>
      <Typography>Triggers are used to initiate system flows when the attribute value is true.</Typography>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Field fullWidth>
            {label && <Field.Label>{label}</Field.Label>}
            <Select
              value={trigger || null}
              onValueChange={(value) => {
                setTrigger(value || "");
              }}
              options={options}
              placeholder={label || "Select a trigger"}
              disabled={options.length === 0}
            />
          </Field>
        </div>
        {canEdit && trigger && (
          <Button
            onClick={() => deleteTrigger(trigger)}
            label="Delete trigger"
            icon
          >
            <Icon name="delete" />
          </Button>
        )}
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={!canEdit}
                label="Add"
              >
                Add
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={4}
              align="end"
            >
              <DropdownMenuItem onClick={() => addTrigger("task")}>{t("flow.task")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTrigger("notification")}>Notification</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTrigger("communication")}>{t("flow.communication")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTrigger("integration")}>{t("flow.integration")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTrigger("storage")}>{t("flow.storage")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTrigger("contact")}>{t("flow.contact")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => addTrigger("entity")}>{t("flow.entity")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {/* {values.triggers.map((t, i) => (<div>trigger {i}</div>))} */}
      <div className={"h-full flex-1 overflow-auto"}>
        {trigger.length > 0 ? <TriggerForm scope={trigger} /> : <Typography>Select a trigger to edit</Typography>}
      </div>
    </div>
  );
};
